import { useState, useEffect, useCallback } from 'react';
import FamilyAiPanel from '../components/family/FamilyAiPanel';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, UserPlus, Copy, CheckCircle, Clock, Target, ChevronRight, X, 
  TrendingUp, GraduationCap, Briefcase, AlertCircle, Focus, Link2,
  DollarSign, BookOpen, Shield, Rocket, Wrench, Award, Brain, 
  BarChart3, PiggyBank, Calculator, Calendar, FileText, Lightbulb,
  Star, ChevronDown, Zap, Home, Download, Printer, FileSpreadsheet,
  ChevronUp, Eye, RefreshCw, CreditCard, Wallet, AlertTriangle, 
  ShieldAlert, ExternalLink, User, MapPin, Sparkles, Lock, Play,
  CheckSquare, Square, ArrowRight, Bell, CalendarDays, FolderOpen,
  Share2, Send, School, ClipboardList, StickyNote, Plus, HelpCircle,
  MessageSquare, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { showFriendlyError } from '../utils/friendlyErrors';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import GuidedTour, { useTour } from '../components/GuidedTour';
import WorkforcePellCallout from '../components/WorkforcePellCallout';
import { useActiveChild } from '../context/ActiveChildContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Path configurations
const PATH_CONFIGS = {
  college: { icon: GraduationCap, label: 'College Bound', gradient: 'from-blue-600 to-cyan-600', color: 'blue' },
  trade: { icon: Wrench, label: 'Skilled Trades', gradient: 'from-amber-600 to-orange-600', color: 'amber' },
  military: { icon: Shield, label: 'Military Service', gradient: 'from-green-600 to-emerald-600', color: 'green' },
  entrepreneurship: { icon: Rocket, label: 'Entrepreneurship', gradient: 'from-purple-600 to-pink-600', color: 'purple' },
  workforce: { icon: Briefcase, label: 'Direct Workforce', gradient: 'from-slate-600 to-zinc-600', color: 'slate' }
};

// Format helpers
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Calculate days until graduation (assumes May graduation)
const calculateGraduationCountdown = (gradeLevel) => {
  if (!gradeLevel) return null;
  const currentYear = new Date().getFullYear();
  const graduationYear = currentYear + (12 - gradeLevel);
  const graduationDate = new Date(graduationYear, 4, 15); // May 15
  const today = new Date();
  const diffTime = graduationDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return { days: diffDays, year: graduationYear, date: graduationDate };
};

// Welcome Banner Component
const WelcomeBanner = ({ userName, onStartTour }) => {
  return (
    <GlassCard className="p-6 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 border-blue-500/30">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white mb-2">
            Welcome to Your Family Dashboard, {userName?.split(' ')[0] || 'Parent'}! 👋
          </h2>
          <p className="text-slate-300 mb-4 max-w-2xl">
            Agentic FuturePath AI helps you <strong className="text-white">guide and support your children's educational journey</strong>. 
            Here you can track their progress, explore pathway options together, plan financially, and provide input on their decisions.
          </p>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Track Decisions</p>
                <p className="text-slate-300 text-xs">Review & support their path choices</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Research Together</p>
                <p className="text-slate-300 text-xs">Explore colleges, trades & more</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Plan Financially</p>
                <p className="text-slate-300 text-xs">Understand costs & funding</p>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={onStartTour}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500"
          >
            <Play className="w-4 h-4 mr-2" /> Take a Quick Tour
          </Button>
        </div>
      </div>
    </GlassCard>
  );
};

// Recent Activity Feed Component
// Parent sync, shows career experiences each linked child has saved (informational only)
const SavedExperiencesFeed = ({ children, getAuthHeader }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!children || children.length === 0) { setLoaded(true); return; }
    Promise.all(children.map((c) =>
      axios.get(`${API}/experiences/saved?child_id=${c.child_id || c.id}`, getAuthHeader())
        .then((r) => (r.data.saved || []).map((s) => ({ ...s, childName: c.name || c.full_name || 'Your child' })))
        .catch(() => [])
    )).then((arrs) => { setItems(arrs.flat()); setLoaded(true); });
  }, [children, getAuthHeader]);

  if (!loaded) return null;
  return (
    <GlassCard className="p-6" data-testid="parent-saved-experiences">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center"><Rocket className="w-5 h-5" /></div>
        <h3 className="text-lg font-bold text-white">Saved Career Experiences</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-slate-300 text-sm">When your child saves an internship, externship, or apprenticeship, it will appear here.</p>
      ) : (
        <div className="space-y-3">
          {items.map((s, i) => {
            const e = s.experience || {};
            const deadline = e.application_deadline ? new Date(e.application_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Rolling';
            return (
              <div key={i} className="rounded-xl bg-slate-800/40 border border-slate-700/40 p-3" data-testid="parent-saved-experience-item">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{e.title}</p>
                    <p className="text-slate-300 text-xs">{e.organization} · <span className="capitalize">{e.experience_type}</span>{e.state ? ` · ${e.state}` : ''}</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 whitespace-nowrap">{s.childName}</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-300">
                  <span>Due {deadline}</span>
                  <span>· Saved {s.saved_at ? new Date(s.saved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
};


const RecentActivityFeed = ({ children, getAuthHeader }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivities = useCallback(async () => {
    try {
      // Hit the unified /api/activity/feed which merges events across ALL
      // linked children. This is the same stream Sara / FamilyReport use,
      // so dashboards stay in sync with the rest of the platform.
      const res = await axios.get(`${API}/activity/feed?limit=25`, {
        ...getAuthHeader(),
        withCredentials: true,
      });
      const items = (res.data.items || []).map((e) => ({
        // shape kept compatible with the existing render code below
        action_type: e.event_type,
        description: e.title || e.summary || e.event_type,
        summary: e.summary,
        icon_emoji: e.icon,
        timestamp: e.created_at,
        child_name: e.student_name,
        child_id: e.student_id,
        id: e.id,
      }));
      setActivities(items.slice(0, 15));
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeader]);

  useEffect(() => {
    if (!children || children.length === 0) {
      setLoading(false);
      return;
    }
    fetchActivities();
    // Live sync, poll every 15s and on tab focus so events from the
    // child's session appear here without the parent refreshing.
    const t = setInterval(fetchActivities, 15_000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchActivities(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [children, fetchActivities]);

  const getActivityIcon = (type, emoji) => {
    if (emoji) return null;  // Use the emoji from the backend if present
    const icons = {
      'college_view': GraduationCap, 'college.saved': GraduationCap,
      'career_explore': Briefcase,
      'assessment_complete': CheckCircle, 'personality.completed': CheckCircle,
      'goal_complete': Target, 'goal.updated': Target,
      'path_select': Lock, 'decision.finalized': Lock, 'decision.created': Lock,
      'profile_update': User, 'profile.updated': User,
      'roadmap.updated': Zap, 'milestone.completed': Target,
      'default': Zap,
    };
    return icons[type] || icons.default;
  };
  
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  return (
    <GlassCard className="p-6" data-testid="recent-activity-feed">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-400" />
          Recent Activity
        </h3>
        <Button variant="ghost" size="sm" onClick={fetchActivities} className="text-slate-300 hover:text-slate-200">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-6">
          <RefreshCw className="w-6 h-6 text-slate-300 animate-spin mx-auto" />
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-8">
          <Zap className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-300">No recent activity yet</p>
          <p className="text-slate-300 text-sm">Your children's activities will appear here</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[300px] overflow-y-auto">
          {activities.map((activity, idx) => {
            const Icon = getActivityIcon(activity.action_type, activity.icon_emoji);
            return (
              <div key={activity.id || idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  {activity.icon_emoji ? (
                    <span className="text-base">{activity.icon_emoji}</span>
                  ) : Icon ? (
                    <Icon className="w-4 h-4 text-blue-400" />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">
                    <span className="font-medium text-blue-300">{activity.child_name}</span>
                    {' '}{activity.description || activity.action_type?.replace(/[._]/g, ' ')}
                  </p>
                  {activity.summary ? (
                    <p className="text-slate-300 text-xs mt-0.5 truncate">{activity.summary}</p>
                  ) : null}
                  <p className="text-slate-300 text-xs mt-0.5">{formatTimeAgo(activity.timestamp)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
};

// Family Conversation Starters Component
const ConversationStarters = ({ children }) => {
  const [starters, setStarters] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    generateStarters();
    // Auto-rotate the prompts every 60s so the parent sees variety
    // and stays in sync when child data refreshes elsewhere on the page.
    const t = setInterval(generateStarters, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);
  
  const generateStarters = () => {
    if (!children || children.length === 0) return;
    
    const generated = [];
    
    children.forEach(child => {
      const firstName = child.name?.split(' ')[0] || 'your child';
      const pathType = child.focus_mode?.path_type;
      const pathName = child.focus_mode?.path_name;
      const interests = child.interests || [];
      const grade = child.grade_level;
      
      // Path-specific starters
      if (pathType === 'college' && pathName) {
        generated.push({
          child_name: firstName,
          icon: GraduationCap,
          color: 'blue',
          text: `Ask ${firstName} what excites them most about ${pathName}`,
          context: 'They selected this as their top choice'
        });
      } else if (pathType === 'trade') {
        generated.push({
          child_name: firstName,
          icon: Wrench,
          color: 'amber',
          text: `Talk to ${firstName} about apprenticeship opportunities in your area`,
          context: 'Exploring skilled trades path'
        });
      } else if (pathType === 'military') {
        generated.push({
          child_name: firstName,
          icon: Shield,
          color: 'green',
          text: `Discuss with ${firstName} which military branch interests them and why`,
          context: 'Considering military service'
        });
      } else if (pathType === 'entrepreneurship') {
        generated.push({
          child_name: firstName,
          icon: Rocket,
          color: 'purple',
          text: `Ask ${firstName} about their business idea or what problem they want to solve`,
          context: 'Interested in entrepreneurship'
        });
      }
      
      // Interest-based starters
      if (interests.length > 0) {
        const interest = interests[0];
        generated.push({
          child_name: firstName,
          icon: Lightbulb,
          color: 'cyan',
          text: `${firstName} is interested in ${interest}, ask how they see it shaping their future`,
          context: 'Based on their interests'
        });
      }
      
      // Grade-specific starters
      if (grade >= 11) {
        generated.push({
          child_name: firstName,
          icon: Calendar,
          color: 'pink',
          text: `Check in with ${firstName} about their timeline, are they feeling prepared?`,
          context: `Grade ${grade} - decisions coming up`
        });
      } else if (grade <= 8) {
        generated.push({
          child_name: firstName,
          icon: Star,
          color: 'yellow',
          text: `Encourage ${firstName} to explore different career paths without pressure`,
          context: 'Early exploration phase'
        });
      }
      
      // General starters if we don't have specific ones
      if (!pathType && interests.length === 0) {
        generated.push({
          child_name: firstName,
          icon: MessageSquare,
          color: 'slate',
          text: `Ask ${firstName} what they've been thinking about for their future`,
          context: 'Start the conversation'
        });
      }
    });
    
    // Shuffle and take top 4
    const shuffled = generated.sort(() => Math.random() - 0.5);
    setStarters(shuffled.slice(0, 4));
    setRefreshKey(prev => prev + 1);
  };
  
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    slate: 'bg-slate-500/20 text-slate-300 border-slate-500/30'
  };
  
  if (!children || children.length === 0 || starters.length === 0) return null;
  
  return (
    <GlassCard className="p-6" data-testid="conversation-starters">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-pink-400" />
          Conversation Starters
        </h3>
        <Button variant="ghost" size="sm" onClick={generateStarters} className="text-slate-300 hover:text-slate-200">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      <p className="text-slate-300 text-sm mb-4">
        Personalized prompts to help you connect with your children about their future
      </p>
      
      <div className="space-y-3">
        {starters.map((starter, idx) => {
          const Icon = starter.icon;
          return (
            <div 
              key={`${refreshKey}-${idx}`} 
              className={`p-4 rounded-xl border ${colorClasses[starter.color]} transition-all hover:scale-[1.02]`}
            >
              <div className="flex items-start gap-3">
                <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">{starter.text}</p>
                  <p className="text-slate-300 text-xs mt-1">{starter.context}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
};

// Child Summary Card Component
const ChildSummaryCard = ({ child, onClick, onResendCredentials }) => {
  const navigate = useNavigate();
  const pathConfig = PATH_CONFIGS[child.focus_mode?.path_type];
  const PathIcon = pathConfig?.icon || Target;
  const graduation = calculateGraduationCountdown(child.grade_level);
  const [resending, setResending] = useState(false);

  const handleResend = async (e) => {
    e.stopPropagation();
    if (resending) return;
    setResending(true);
    try {
      await onResendCredentials?.(child);
    } finally {
      setResending(false);
    }
  };
  
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="cursor-pointer"
    >
      <GlassCard className="p-5 h-full hover:border-blue-500/50 transition-all">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl bg-gradient-to-br ${pathConfig?.gradient || 'from-slate-600 to-slate-700'}`}>
              {child.name?.charAt(0).toUpperCase() || '?'}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{child.name}</h3>
              <p className="text-slate-300 text-sm">Grade {child.grade_level || '-'}</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-lg text-xs font-medium bg-gradient-to-r ${pathConfig?.gradient || 'from-slate-600 to-slate-700'} text-white`}>
            <PathIcon className="w-3 h-3 inline mr-1" />
            {pathConfig?.label || 'Exploring'}
          </div>
        </div>
        
        {/* Graduation Countdown */}
        {graduation && graduation.days > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-slate-300">Graduation Countdown</span>
              </div>
              <div className="text-right">
                <span className="text-xl font-bold text-purple-400">{graduation.days}</span>
                <span className="text-slate-300 text-sm ml-1">days</span>
              </div>
            </div>
            <p className="text-xs text-slate-300 mt-1">Class of {graduation.year}</p>
          </div>
        )}
        
        {/* Selected School/Path */}
        {child.focus_mode?.path_name ? (
          <div className="mb-4 p-3 rounded-lg bg-slate-800/50">
            <p className="text-xs text-slate-300 mb-1">Selected Institution</p>
            <p className="text-white font-medium truncate">{child.focus_mode.path_name}</p>
            {child.focus_mode.major_or_track && (
              <p className="text-slate-300 text-sm">{child.focus_mode.major_or_track}</p>
            )}
          </div>
        ) : (
          <div className="mb-4 p-3 rounded-lg bg-slate-800/30 border border-dashed border-slate-700">
            <p className="text-slate-300 text-sm text-center">No institution selected yet</p>
          </div>
        )}
        
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center p-2 rounded-lg bg-slate-800/30">
            <p className={`text-lg font-bold ${child.cri_score >= 70 ? 'text-emerald-400' : child.cri_score >= 40 ? 'text-yellow-400' : 'text-slate-300'}`}>
              {child.cri_score?.toFixed(0) || '-'}
            </p>
            <p className="text-xs text-slate-300">Readiness</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-slate-800/30">
            <p className="text-lg font-bold text-blue-400">{child.goals?.completed || 0}/{child.goals?.total || 0}</p>
            <p className="text-xs text-slate-300">Goals</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-slate-800/30">
            <p className="text-lg font-bold text-purple-400">{child.profile_completion || 0}%</p>
            <p className="text-xs text-slate-300">Profile</p>
          </div>
        </div>
        
        {/* Financial Summary */}
        {child.financial_summary && (
          <div className="pt-3 border-t border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-300 text-sm">Est. Cost</span>
              <span className="text-white font-medium">{formatCurrency(child.financial_summary.total_cost)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm">Funding Gap</span>
              <span className={`font-medium ${child.financial_summary.funding_gap > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {formatCurrency(child.financial_summary.funding_gap)}
              </span>
            </div>
          </div>
        )}
        
        <Button variant="ghost" className="w-full mt-4 text-blue-400 hover:text-blue-300" data-testid={`child-${child.id}-view-details-btn`}>
          View Details <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => { e.stopPropagation(); navigate('/app/parent/child-cdp'); }}
          className="w-full mt-2 border-cyan-500/40 hover:border-cyan-400/60 text-cyan-300 hover:text-cyan-200 bg-cyan-500/[0.06]"
          data-testid={`child-${child.id}-view-plan-btn`}
        >
          View FuturePath Plan <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleResend}
          disabled={resending}
          className="w-full mt-2 border-slate-700 hover:border-emerald-500/50 text-slate-300 hover:text-emerald-400 bg-slate-900/40"
          data-testid={`child-${child.id}-resend-credentials-btn`}
        >
          <Mail className="w-4 h-4 mr-2" />
          {resending ? 'Sending...' : 'Resend Login Email'}
        </Button>
      </GlassCard>
    </motion.div>
  );
};

// Financial Overview Widget
const FinancialOverviewWidget = ({ familySummary, portfolio }) => {
  return (
    <GlassCard className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-400" />
          Financial Overview
        </h3>
        <Button variant="ghost" size="sm" className="text-blue-400">
          View Details <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
          <p className="text-slate-300 text-sm mb-1">Total Education Cost</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(familySummary?.total_education_exposure)}</p>
        </div>
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
          <p className="text-slate-300 text-sm mb-1">Current Savings</p>
          <p className="text-2xl font-bold text-emerald-400">{formatCurrency(familySummary?.total_current_savings)}</p>
        </div>
      </div>
      
      <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-300">Funding Gap</span>
          <span className="text-amber-400 font-bold">{formatCurrency(familySummary?.total_funding_shortfall)}</span>
        </div>
        <Progress 
          value={((familySummary?.total_current_savings || 0) / (familySummary?.total_education_exposure || 1)) * 100} 
          className="h-2"
        />
        <p className="text-xs text-slate-300 mt-2">
          {((familySummary?.total_current_savings || 0) / (familySummary?.total_education_exposure || 1) * 100).toFixed(0)}% funded
        </p>
      </div>
      
      <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
        <div>
          <p className="text-slate-300 text-sm">Monthly Savings Rate</p>
          <p className="text-white font-medium">{formatCurrency(portfolio?.monthly_savings_rate || 0)}/mo</p>
        </div>
        <div>
          <p className="text-slate-300 text-sm">Recommended</p>
          <p className="text-blue-400 font-medium">{formatCurrency(familySummary?.monthly_contribution_required || 0)}/mo</p>
        </div>
      </div>
    </GlassCard>
  );
};

// Action Items Widget
const ActionItemsWidget = ({ actionItems, onToggle, onRefresh }) => {
  const incompleteItems = actionItems?.filter(item => !item.completed) || [];
  const completedItems = actionItems?.filter(item => item.completed) || [];
  
  const priorityColors = {
    high: 'text-red-400 bg-red-500/10 border-red-500/20',
    medium: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    low: 'text-blue-400 bg-blue-500/10 border-blue-500/20'
  };
  
  const categoryIcons = {
    financial: DollarSign,
    academic: GraduationCap,
    deadline: Calendar,
    document: FileText
  };
  
  return (
    <GlassCard className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-blue-400" />
          Your Action Items
        </h3>
        <Button variant="ghost" size="sm" onClick={onRefresh} className="text-slate-300">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      {incompleteItems.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-slate-300">All caught up! No pending action items.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {incompleteItems.slice(0, 5).map((item) => {
            const CategoryIcon = categoryIcons[item.category] || CheckSquare;
            return (
              <div 
                key={item.id}
                className={`p-4 rounded-xl border ${priorityColors[item.priority] || priorityColors.medium} cursor-pointer hover:opacity-80 transition-opacity`}
                onClick={() => onToggle(item.id)}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <Square className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CategoryIcon className="w-4 h-4" />
                      <span className="text-white font-medium">{item.title}</span>
                    </div>
                    <p className="text-slate-300 text-sm">{item.description}</p>
                    {item.due_date && (
                      <p className="text-xs text-slate-300 mt-2 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Due: {formatDate(item.due_date)}
                      </p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${priorityColors[item.priority]}`}>
                    {item.priority}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {completedItems.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-700">
          <p className="text-slate-300 text-sm mb-2">{completedItems.length} completed</p>
        </div>
      )}
    </GlassCard>
  );
};

// Scholarships & Aid Widget
const ScholarshipsWidget = ({ children, onShare }) => {
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Generate sample scholarships based on children's profiles
    const sampleScholarships = [
      { id: '1', name: 'National Merit Scholarship', amount: '$2,500', deadline: '2025-10-15', eligibility: 'High academic achievement', match_score: 92, child_match: children[0]?.name },
      { id: '2', name: 'Gates Scholarship', amount: 'Full Tuition', deadline: '2025-09-15', eligibility: 'Leadership & community service', match_score: 85, child_match: children[0]?.name },
      { id: '3', name: 'Dell Scholars Program', amount: '$20,000', deadline: '2025-12-01', eligibility: 'First-generation college students', match_score: 78, child_match: children[0]?.name },
      { id: '4', name: 'Coca-Cola Scholars', amount: '$20,000', deadline: '2025-10-31', eligibility: 'Community leadership', match_score: 75, child_match: children.length > 1 ? children[1]?.name : children[0]?.name },
    ];
    setScholarships(sampleScholarships);
    setLoading(false);
  }, [children]);
  
  const handleShareWithChild = (scholarship, childName) => {
    onShare(scholarship, childName);
    toast.success(`Scholarship shared with ${childName}!`);
  };
  
  return (
    <GlassCard className="p-6" data-testid="scholarships-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-400" />
          Scholarships & Aid
        </h3>
        <Button variant="ghost" size="sm" className="text-blue-400 text-sm">
          View All <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-6">
          <RefreshCw className="w-8 h-8 text-slate-300 animate-spin mx-auto" />
        </div>
      ) : scholarships.length === 0 ? (
        <div className="text-center py-8">
          <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-300">No scholarship matches yet</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[350px] overflow-y-auto">
          {scholarships.map((scholarship) => (
            <div key={scholarship.id} className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-amber-500/30 transition-all">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="text-white font-medium">{scholarship.name}</h4>
                  <p className="text-amber-400 font-semibold">{scholarship.amount}</p>
                </div>
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${scholarship.match_score >= 80 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`}>
                  {scholarship.match_score}% match
                </span>
              </div>
              <p className="text-slate-300 text-sm mb-2">{scholarship.eligibility}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-300 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Deadline: {formatDate(scholarship.deadline)}
                </span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => handleShareWithChild(scholarship, scholarship.child_match)}
                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                  data-testid={`share-scholarship-${scholarship.id}`}
                >
                  <Share2 className="w-4 h-4 mr-1" /> Share with {scholarship.child_match?.split(' ')[0]}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

// Application Tracker Widget
const ApplicationTrackerWidget = ({ children, getAuthHeader }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchApplications();
  }, [children]);
  
  const fetchApplications = async () => {
    if (!children || children.length === 0) {
      setLoading(false);
      return;
    }
    
    try {
      // Fetch applications for all children
      const allApps = [];
      for (const child of children) {
        try {
          const res = await axios.get(`${API}/applications/${child.id}`, {
            ...getAuthHeader(),
            withCredentials: true
          });
          const apps = (res.data.applications || []).map(app => ({ ...app, child_name: child.name }));
          allApps.push(...apps);
        } catch (e) {
          console.log(`No applications for ${child.name}`);
        }
      }
      setApplications(allApps);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const statusColors = {
    not_started: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    action_required: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    submitted: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    complete: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
  };
  
  const statusLabels = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    action_required: 'Action Required',
    submitted: 'Submitted',
    complete: 'Complete'
  };
  
  return (
    <GlassCard className="p-6" data-testid="application-tracker-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-blue-400" />
          Application Tracker
        </h3>
      </div>
      
      {loading ? (
        <div className="text-center py-6">
          <RefreshCw className="w-8 h-8 text-slate-300 animate-spin mx-auto" />
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-8">
          <School className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-300">No applications tracked yet</p>
          <p className="text-slate-300 text-sm mt-1">Your children will add applications as they begin</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[300px] overflow-y-auto">
          {applications.slice(0, 5).map((app) => (
            <div key={app.id} className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/50">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="text-white font-medium">{app.institution_name}</h4>
                  <p className="text-slate-300 text-sm">{app.child_name}</p>
                </div>
                <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${statusColors[app.overall_status] || statusColors.not_started}`}>
                  {statusLabels[app.overall_status] || 'Not Started'}
                </span>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-300">Completion</span>
                  <span className="text-xs text-slate-300">{app.completion_percent || 0}%</span>
                </div>
                <Progress value={app.completion_percent || 0} className="h-2" />
              </div>
              {app.application_deadline && (
                <p className="text-xs text-slate-300 mt-2 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Deadline: {formatDate(app.application_deadline)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

// Things to Remember Widget (replaces Test Dates)
const ThingsToRememberWidget = ({ children, onAddReminder }) => {
  const [reminders, setReminders] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReminder, setNewReminder] = useState({ title: '', date: '', child_id: '' });
  
  useEffect(() => {
    // Initialize with some sample reminders based on children's milestones
    const sampleReminders = [
      { id: '1', title: 'FAFSA Opens', date: '2025-10-01', category: 'deadline', child_name: 'All Children', priority: 'high' },
      { id: '2', title: 'Early Decision Deadline', date: '2025-11-01', category: 'application', child_name: children[0]?.name, priority: 'high' },
      { id: '3', title: 'SAT Exam Date', date: '2025-03-08', category: 'exam', child_name: children[0]?.name, priority: 'medium' },
      { id: '4', title: 'Schedule Campus Visits', date: '2025-06-15', category: 'task', child_name: 'All Children', priority: 'low' },
    ];
    setReminders(sampleReminders);
  }, [children]);
  
  const priorityColors = {
    high: 'text-red-400 border-red-500/30 bg-red-500/10',
    medium: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
    low: 'text-blue-400 border-blue-500/30 bg-blue-500/10'
  };
  
  const categoryIcons = {
    deadline: Calendar,
    application: FileText,
    test: BookOpen,
    task: CheckSquare
  };
  
  const handleAddReminder = () => {
    if (!newReminder.title || !newReminder.date) {
      toast.error('Please fill in title and date');
      return;
    }
    
    const reminder = {
      id: Date.now().toString(),
      ...newReminder,
      child_name: newReminder.child_id ? children.find(c => c.id === newReminder.child_id)?.name : 'All Children',
      category: 'task',
      priority: 'medium'
    };
    setReminders(prev => [...prev, reminder]);
    setNewReminder({ title: '', date: '', child_id: '' });
    setShowAddForm(false);
    toast.success('Reminder added!');
  };
  
  return (
    <GlassCard className="p-6" data-testid="things-to-remember-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <StickyNote className="w-5 h-5 text-purple-400" />
          Things to Remember
        </h3>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={() => setShowAddForm(!showAddForm)}
          className="text-purple-400 hover:text-purple-300"
          data-testid="add-reminder-btn"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Add Reminder Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4"
          >
            <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20 space-y-3">
              <input
                type="text"
                placeholder="What do you need to remember?"
                value={newReminder.title}
                onChange={(e) => setNewReminder(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
              />
              <div className="flex gap-2">
                <input
                  type="date"
                  value={newReminder.date}
                  onChange={(e) => setNewReminder(prev => ({ ...prev, date: e.target.value }))}
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                />
                <select
                  value={newReminder.child_id}
                  onChange={(e) => setNewReminder(prev => ({ ...prev, child_id: e.target.value }))}
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                >
                  <option value="">All Children</option>
                  {children.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <Button size="sm" onClick={handleAddReminder} className="w-full bg-purple-600 hover:bg-purple-700">
                Add Reminder
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {reminders.length === 0 ? (
        <div className="text-center py-8">
          <StickyNote className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-300">No reminders yet</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[300px] overflow-y-auto">
          {reminders.sort((a, b) => new Date(a.date) - new Date(b.date)).map((reminder) => {
            const CategoryIcon = categoryIcons[reminder.category] || StickyNote;
            const daysUntil = Math.ceil((new Date(reminder.date) - new Date()) / (1000 * 60 * 60 * 24));
            
            return (
              <div 
                key={reminder.id} 
                className={`p-3 rounded-xl border ${priorityColors[reminder.priority] || priorityColors.medium}`}
              >
                <div className="flex items-start gap-3">
                  <CategoryIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{reminder.title}</p>
                    <p className="text-slate-300 text-xs">{reminder.child_name}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-xs font-medium ${daysUntil <= 7 ? 'text-red-400' : daysUntil <= 30 ? 'text-amber-400' : 'text-slate-300'}`}>
                      {daysUntil <= 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
                    </p>
                    <p className="text-xs text-slate-300">{formatDate(reminder.date)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
};

// Child Detail Modal
const ChildDetailModal = ({ isOpen, onClose, child, getAuthHeader }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (isOpen && child) {
      fetchChildDetails();
    }
  }, [isOpen, child]);
  
  const fetchChildDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/family/child/${child.id}/details`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setDetails(res.data);
    } catch (error) {
      console.error('Failed to fetch child details:', error);
      showFriendlyError(error, { context: 'Loading details', fallback: "We couldn't load the latest details. Please try again in a moment." });
    } finally {
      setLoading(false);
    }
    // Fetch portfolio summary
    try {
      const portfolioRes = await axios.get(`${API}/portfolio/child/${child.id}/summary`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setPortfolioData(portfolioRes.data);
    } catch {
      setPortfolioData(null);
    }
  };
  
  if (!isOpen) return null;
  
  const pathConfig = PATH_CONFIGS[child?.focus_mode?.path_type];
  const PathIcon = pathConfig?.icon || Target;
  
  return (
    <div className="fixed inset-0 bg-[#0a1729]/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-white font-bold text-2xl bg-gradient-to-br ${pathConfig?.gradient || 'from-slate-600 to-slate-700'}`}>
              {child?.name?.charAt(0).toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{child?.name}</h2>
              <p className="text-slate-300">Grade {child?.grade_level || '-'}</p>
              {pathConfig && (
                <span className={`inline-flex items-center gap-1 mt-1 px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r ${pathConfig.gradient} text-white`}>
                  <PathIcon className="w-3 h-3" /> {pathConfig.label}
                </span>
              )}
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">
            <X className="w-6 h-6" />
          </Button>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto" />
            <p className="text-slate-300 mt-3">Loading details...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-slate-800/50 text-center">
                <p className={`text-2xl font-bold ${(details?.cri_score || child?.cri_score) >= 70 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {(details?.cri_score || child?.cri_score)?.toFixed(0) || '-'}
                </p>
                <p className="text-slate-300 text-sm">Readiness Score</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/50 text-center">
                <p className="text-2xl font-bold text-blue-400">
                  {details?.goals_completed || child?.goals?.completed || 0}/{details?.goals_total || child?.goals?.total || 0}
                </p>
                <p className="text-slate-300 text-sm">Goals Completed</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/50 text-center">
                <p className="text-2xl font-bold text-purple-400">{details?.profile_completion || child?.profile_completion || 0}%</p>
                <p className="text-slate-300 text-sm">Profile Complete</p>
              </div>
            </div>
            
            {/* Selected Path */}
            {(details?.focus_mode?.path_name || child?.focus_mode?.path_name) && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
                <p className="text-slate-300 text-sm mb-1">Selected Institution</p>
                <p className="text-white font-semibold text-lg">{details?.focus_mode?.path_name || child?.focus_mode?.path_name}</p>
                {(details?.focus_mode?.major_or_track || child?.focus_mode?.major_or_track) && (
                  <p className="text-blue-300">{details?.focus_mode?.major_or_track || child?.focus_mode?.major_or_track}</p>
                )}
              </div>
            )}
            
            {/* Interests */}
            {(details?.interests || []).length > 0 && (
              <div>
                <h4 className="text-slate-300 font-medium mb-2">Interests</h4>
                <div className="flex flex-wrap gap-2">
                  {details.interests.map((interest, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Recent Activity */}
            {(details?.recent_activity || []).length > 0 && (
              <div>
                <h4 className="text-slate-300 font-medium mb-2">Recent Activity</h4>
                <div className="space-y-2">
                  {details.recent_activity.slice(0, 5).map((activity, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/30">
                      <div className="w-2 h-2 bg-blue-400 rounded-full" />
                      <span className="text-slate-300 text-sm flex-1">{activity.description || activity.action_type}</span>
                      <span className="text-slate-300 text-xs">{formatDate(activity.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Financial Summary */}
            {(details?.financial_summary || child?.financial_summary) && (
              <div className="p-4 rounded-xl bg-slate-800/50">
                <h4 className="text-slate-300 font-medium mb-3">Financial Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-300 text-sm">Estimated Cost</p>
                    <p className="text-white font-semibold">{formatCurrency((details?.financial_summary || child?.financial_summary)?.total_cost)}</p>
                  </div>
                  <div>
                    <p className="text-slate-300 text-sm">Funding Gap</p>
                    <p className={`font-semibold ${(details?.financial_summary || child?.financial_summary)?.funding_gap > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {formatCurrency((details?.financial_summary || child?.financial_summary)?.funding_gap)}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Academic Portfolio */}
            {portfolioData && !portfolioData.access_denied && (
              <div className="p-4 rounded-xl bg-slate-800/50" data-testid="child-portfolio-section">
                <h4 className="text-slate-300 font-medium mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-blue-400" /> Academic Portfolio
                </h4>
                {portfolioData.latest_rating ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className={`text-3xl font-bold ${portfolioData.latest_rating.overall_score >= 80 ? 'text-emerald-400' : portfolioData.latest_rating.overall_score >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                          {portfolioData.latest_rating.overall_score}
                        </div>
                        <div className="text-xs text-slate-300">Score</div>
                      </div>
                      <div className="flex-1 space-y-1.5">
                        {portfolioData.latest_rating.sub_scores && Object.entries(portfolioData.latest_rating.sub_scores).map(([key, val]) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="text-xs text-slate-300 w-24 capitalize">{key.replace('_', ' ')}</span>
                            <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${val >= 70 ? 'bg-emerald-500' : val >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${val}%` }} />
                            </div>
                            <span className="text-xs text-slate-300 w-6 text-right">{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-3 text-xs text-slate-300">
                      <span>{portfolioData.record_count || 0} records</span>
                      <span>{portfolioData.document_count || 0} documents</span>
                    </div>
                    {/* Milestones */}
                    {portfolioData.milestones?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-700">
                        {portfolioData.milestones.map(ms => (
                          <span key={ms.milestone_key} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border"
                            style={{ borderColor: ms.badge_color, color: ms.badge_color, backgroundColor: `${ms.badge_color}10` }}>
                            <CheckCircle className="w-3 h-3" /> {ms.title}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Parent Recommendations */}
                    {portfolioData.parent_recommendations?.length > 0 && (
                      <div className="pt-2 border-t border-slate-700">
                        <p className="text-xs text-amber-400 mb-1.5 flex items-center gap-1"><Star className="w-3 h-3" /> For You</p>
                        {portfolioData.parent_recommendations.map((rec, i) => (
                          <p key={i} className="text-xs text-slate-300 mb-1 pl-4">• {rec}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-300 text-sm text-center py-3">No portfolio data yet</p>
                )}
              </div>
            )}
          </div>
        )}
        {/* CTA: View full plan */}
        <div className="pt-4 border-t border-slate-700 flex gap-3">
          <Button
            onClick={() => { onClose(); navigate('/app/parent/child-cdp'); }}
            className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
            data-testid="modal-view-plan-btn"
          >
            View Full FuturePath Plan <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">
            Close
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// AI Advisor Widget, Always functional, no premium gate
const AIAdvisorWidget = ({ advice, onGetAdvice, loading }) => {
  return (
    <GlassCard className="p-6" data-testid="ai-advisor-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">AI Parent Advisor</h3>
            <p className="text-slate-300 text-sm">Personalized guidance for your family</p>
          </div>
        </div>
        <Button 
          onClick={onGetAdvice} 
          disabled={loading}
          size="sm"
          className="bg-gradient-to-r from-purple-600 to-pink-600"
          data-testid="get-advice-btn"
        >
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {loading ? 'Analyzing...' : 'Get Advice'}
        </Button>
      </div>
      
      {advice ? (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
            <p className="text-white">{advice.summary}</p>
          </div>
          
          {advice.strengths?.length > 0 && (
            <div>
              <h4 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> What's Going Well
              </h4>
              <ul className="space-y-1">
                {advice.strengths.map((item, idx) => (
                  <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {advice.concerns?.length > 0 && (
            <div>
              <h4 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Areas to Address
              </h4>
              <ul className="space-y-1">
                {advice.concerns.map((item, idx) => (
                  <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {advice.immediate_actions?.length > 0 && (
            <div>
              <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                <ArrowRight className="w-4 h-4" /> Do This Now
              </h4>
              <div className="space-y-2">
                {advice.immediate_actions.slice(0, 3).map((action, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                    <p className="text-white font-medium text-sm">{action.title || action}</p>
                    {action.description && <p className="text-slate-300 text-xs mt-1">{action.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {advice.encouragement && (
            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <p className="text-blue-300 text-sm italic">"{advice.encouragement}"</p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-3" />
          <p className="text-slate-300 text-sm">Click "Get Advice" for personalized AI recommendations based on your children's profiles</p>
        </div>
      )}
    </GlassCard>
  );
};

// Add Child Modal built inline below

// Main Parent Dashboard Component
export default function ParentDashboard() {
  const { user, getAuthHeader } = useAuth();
  const { isSelf } = useActiveChild();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [familySummary, setFamilySummary] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newChildForm, setNewChildForm] = useState({ firstName: '', lastName: '', dob: '', grade: '', email: '' });
  const [addChildLoading, setAddChildLoading] = useState(false);
  const [addChildAttested, setAddChildAttested] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [showChildDetail, setShowChildDetail] = useState(false);
  
  // Tour functionality
  const { showTour, startTour, closeTour } = useTour('parent');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // A parent who hasn't finished the welcome is sent back to it, same as everyone else, but only
  // ever for their own. While they're on a child's profile this would loop them into that child's
  // setup, which is exactly what happened when they pressed Skip.
  useEffect(() => {
    if (!isSelf) return;
    axios.get(`${API}/welcome/state`, getAuthHeader())
      .then((r) => {
        if (!r.data.basics_complete || r.data.needs_archetype || r.data.should_launch) navigate('/app/welcome');
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelf]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch children
      const childrenRes = await axios.get(`${API}/family/children`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setChildren(childrenRes.data.children || []);
      setPendingInvites(childrenRes.data.pending_invites || []);

      // ALSO fetch incoming invitations sent BY students to this parent's email
      // (the /family/children endpoint only reads from linked_children, it
      // misses the case where a student invited the parent but the parent
      // hasn't accepted yet, so we need to merge in /family/invitations data).
      try {
        const invRes = await axios.get(`${API}/family/invitations`, {
          ...getAuthHeader(),
          withCredentials: true,
        });
        const incoming = (invRes.data.invitations || []).filter(
          (i) => i.status === 'pending' && i.token
        );
        if (incoming.length > 0) {
          // Merge, these are invitations from a child waiting for parent acceptance
          setPendingInvites((prev) => {
            const existingIds = new Set(prev.map((p) => p.student_id || p.child_id));
            const newOnes = incoming
              .filter((i) => !existingIds.has(i.student_id))
              .map((i) => ({
                child_name: i.student_name,
                child_email: i.student_email || '',
                student_id: i.student_id,
                token: i.token,
                relationship: i.relationship,
                status: 'pending_parent_accept',
                expires_at: i.expires_at,
              }));
            return [...prev, ...newOnes];
          });
        }
      } catch (e) {
        console.error('incoming invitations fetch failed:', e);
      }
      
      // Fetch financial data
      try {
        const financialRes = await axios.get(`${API}/parent/financial/dashboard`, {
          ...getAuthHeader(),
          withCredentials: true
        });
        setFamilySummary(financialRes.data.family_summary);
        setPortfolio(financialRes.data.portfolio);
      } catch (e) {
        console.error('Financial data fetch failed:', e);
      }
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChildClick = (child) => {
    setSelectedChild(child);
    setShowChildDetail(true);
  };

  const handleAddChild = async () => {
    const c = newChildForm;
    if (!c.firstName || !c.lastName) { toast.error('First and last name are required'); return; }
    if (!c.dob) { toast.error('Date of birth is required'); return; }
    const dob = new Date(c.dob);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    if (age < 14) { toast.error(`${c.firstName} is ${age}. Must be at least 14.`); return; }
    if (age >= 18) { toast.error(`${c.firstName} is ${age}. Students 18+ register independently.`); return; }
    if (!c.email) { toast.error('Email is required'); return; }
    if (!addChildAttested) { toast.error('Please confirm the information is accurate'); return; }
    
    setAddChildLoading(true);
    try {
      await axios.post(`${API}/family/add-child-registration`, {
        child_first_name: c.firstName,
        child_last_name: c.lastName,
        child_dob: c.dob,
        child_grade_level: c.grade,
        child_email: c.email,
      }, { ...getAuthHeader(), withCredentials: true });
      
      toast.success(`${c.firstName} added! Redirecting to payment...`);
      
      // Redirect to Stripe for extra child payment
      try {
        const checkoutRes = await axios.post(`${API}/billing/create-checkout`, {
          tier: 'extra_child',
          billing_period: 'monthly',
          origin_url: window.location.origin,
        }, getAuthHeader());
        if (checkoutRes.data.url) {
          window.location.href = checkoutRes.data.url;
          return;
        }
      } catch (payErr) {
        console.error('Payment redirect failed:', payErr);
        toast.error('Child added but payment setup failed. Visit Billing to complete.');
      }
      
      setShowAddModal(false);
      setNewChildForm({ firstName: '', lastName: '', dob: '', grade: '', email: '' });
      setAddChildAttested(false);
      fetchDashboardData();
    } catch (err) {
      showFriendlyError(err, { context: 'Add child', fallback: "We couldn't add this child. Please double-check their email and try again." });
    } finally {
      setAddChildLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Guided Tour */}
        <GuidedTour 
          isOpen={showTour} 
          onClose={closeTour} 
          userRole="parent" 
        />
        
        {/* Welcome Banner - Always show for parents */}
        <WelcomeBanner 
          userName={user?.full_name} 
          onStartTour={startTour}
        />
        
        {/* Trial Countdown Banner */}
        {/* Trial banner removed, paid subscriptions only */}
        
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Your Children</h1>
            <p className="text-slate-300">Track their progress and support their journey</p>
          </div>
        </div>

        {/* One wallet, per-child limits, and the add-a-child gate, available any time. */}
        <FamilyAiPanel />
        
        {/* Last Invite Link Banner */}
        <AnimatePresence>
          {lastInviteLink && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <GlassCard className="p-4 border-emerald-500/30 bg-emerald-500/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Link2 className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-white font-medium">Invite Link Created!</p>
                      <p className="text-emerald-300 text-sm break-all">{lastInviteLink}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={async () => {
                        await navigator.clipboard.writeText(lastInviteLink);
                        toast.success('Copied!');
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Copy className="w-4 h-4 mr-2" /> Copy
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setLastInviteLink(null)} className="text-slate-300">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : children.length === 0 ? (
          /* Empty State */
          <GlassCard className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">No Children Added Yet</h2>
            <p className="text-slate-300 max-w-md mx-auto mb-6">
              Add your children to start monitoring their educational journey and provide guidance on their path to success.
            </p>
            <Button
              onClick={() => document.querySelector('[data-testid="family-add-child"]')?.click()}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
              data-testid="add-first-child-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Add Your First Child
            </Button>
          </GlassCard>
        ) : (
          /* Main Dashboard Content */
          <>
            {/* Children Summary Section */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Your Children
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {children.map((child) => (
                  <ChildSummaryCard 
                    key={child.id} 
                    child={child}
                    onClick={() => handleChildClick(child)}
                    onResendCredentials={async (c) => {
                      try {
                        const res = await axios.post(
                          `${API}/family/resend-child-credentials/${c.id}`,
                          {},
                          getAuthHeader()
                        );
                        if (res.data?.success) {
                          toast.success(`Login email resent to ${c.email}`);
                        } else {
                          toast.error('Unable to resend right now. Please try again.');
                        }
                      } catch (err) {
                        const msg = err?.response?.data?.detail || 'Unable to resend right now.';
                        toast.error(msg);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
            
            {/* Pending Invites, student-sent invitations waiting for parent acceptance */}
            {pendingInvites.length > 0 && (
              <GlassCard className="p-4 border-amber-500/30 bg-amber-500/10" data-testid="pending-invitations-card">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <p className="text-white font-medium mb-2">
                      {pendingInvites.length} Pending Invitation{pendingInvites.length > 1 ? 's' : ''}
                    </p>
                    <div className="space-y-2">
                      {pendingInvites.map((inv, i) => (
                        <div key={i} className="flex items-center justify-between bg-amber-500/5 rounded-lg p-2" data-testid={`pending-invite-${i}`}>
                          <div>
                            <p className="text-amber-100 text-sm font-medium">{inv.child_name}</p>
                            <p className="text-amber-300/70 text-xs">
                              {inv.status === 'pending_parent_accept'
                                ? `${inv.child_name} sent you an invitation, accept to link accounts`
                                : 'Waiting for your child to accept the invitation'}
                            </p>
                          </div>
                          {inv.status === 'pending_parent_accept' && inv.token && (
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  await axios.post(
                                    `${API}/family/accept-invitation?token=${encodeURIComponent(inv.token)}`,
                                    {},
                                    { ...getAuthHeader(), withCredentials: true }
                                  );
                                  toast.success(`${inv.child_name} is now linked to your account!`);
                                  // Refresh
                                  fetchDashboardData();
                                } catch (err) {
                                  showFriendlyError(err, { context: 'Accept invitation', fallback: "We couldn't accept this invitation. The link may have expired." });
                                }
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 ml-2"
                              data-testid={`accept-invite-btn-${i}`}
                            >
                              Accept
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </GlassCard>
            )}
            
            {/* Two Column Layout: Recent Activity + Conversation Starters */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Activity Feed */}
              <RecentActivityFeed 
                children={children}
                getAuthHeader={getAuthHeader}
              />
              
              {/* Conversation Starters */}
              <ConversationStarters 
                children={children}
              />
            </div>

            {/* Parent sync, saved career experiences from each child */}
            <SavedExperiencesFeed children={children} getAuthHeader={getAuthHeader} />

            {/* OBBBA Workforce Pell discovery, parents see the same funded blue-collar paths */}
            <WorkforcePellCallout />

            {/* Quick Links for Parents */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Quick Actions
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button onClick={() => navigate('/app/parent/intelligence')} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-blue-500/50 transition-all text-left" data-testid="quick-financial">
                  <DollarSign className="w-6 h-6 text-blue-400 mb-2" />
                  <p className="text-white font-medium text-sm">Financial Intelligence</p>
                  <p className="text-slate-300 text-xs mt-1">Plan education costs</p>
                </button>
                <button onClick={() => navigate('/app/tuition-calculator')} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-emerald-500/50 transition-all text-left" data-testid="quick-tuition">
                  <Calculator className="w-6 h-6 text-emerald-400 mb-2" />
                  <p className="text-white font-medium text-sm">Tuition Calculator</p>
                  <p className="text-slate-300 text-xs mt-1">Compare college costs</p>
                </button>
                <button onClick={() => navigate('/app/scholarships')} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-purple-500/50 transition-all text-left" data-testid="quick-scholarships">
                  <Award className="w-6 h-6 text-purple-400 mb-2" />
                  <p className="text-white font-medium text-sm">Scholarships & Grants</p>
                  <p className="text-slate-300 text-xs mt-1">Find free money</p>
                </button>
                <button onClick={() => navigate('/app/plan')} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-amber-500/50 transition-all text-left" data-testid="quick-action-plan">
                  <ClipboardList className="w-6 h-6 text-amber-400 mb-2" />
                  <p className="text-white font-medium text-sm">Action Plans</p>
                  <p className="text-slate-300 text-xs mt-1">AI-powered roadmaps</p>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Add Child Modal */}
      {/* Add Child Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-[#0a1729]/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Add Child</h2>
              <button onClick={() => setShowAddModal(false)} className="text-slate-300 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-amber-300 text-xs font-medium">+$10/month for each additional child beyond your plan</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 block mb-1">First Name <span className="text-red-400">*</span></label>
                  <input value={newChildForm.firstName} onChange={(e) => setNewChildForm(p => ({...p, firstName: e.target.value}))} placeholder="First name" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-300 block mb-1">Last Name <span className="text-red-400">*</span></label>
                  <input value={newChildForm.lastName} onChange={(e) => setNewChildForm(p => ({...p, lastName: e.target.value}))} placeholder="Last name" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 block mb-1">Date of Birth <span className="text-red-400">*</span></label>
                  <input type="date" value={newChildForm.dob} onChange={(e) => setNewChildForm(p => ({...p, dob: e.target.value}))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 outline-none" />
                  {newChildForm.dob && (() => {
                    const d = new Date(newChildForm.dob); const t = new Date();
                    let a = t.getFullYear() - d.getFullYear(); const mo = t.getMonth() - d.getMonth();
                    if (mo < 0 || (mo === 0 && t.getDate() < d.getDate())) a--;
                    return <p className={`text-xs mt-1 ${a >= 14 && a < 18 ? 'text-emerald-400' : 'text-red-400'}`}>Age: {a}</p>;
                  })()}
                </div>
                <div>
                  <label className="text-xs text-slate-300 block mb-1">Grade</label>
                  <select value={newChildForm.grade} onChange={(e) => setNewChildForm(p => ({...p, grade: e.target.value}))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm">
                    <option value="">Select</option>
                    <option value="9th">9th Grade</option>
                    <option value="10th">10th Grade</option>
                    <option value="11th">11th Grade</option>
                    <option value="12th">12th Grade</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-300 block mb-1">Email <span className="text-red-400">*</span></label>
                <input type="email" value={newChildForm.email} onChange={(e) => setNewChildForm(p => ({...p, email: e.target.value}))} placeholder="child@email.com" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 outline-none" />
              </div>
              <label className="flex items-start gap-3 p-3 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer">
                <input type="checkbox" checked={addChildAttested} onChange={(e) => setAddChildAttested(e.target.checked)} className="mt-0.5 w-4 h-4 rounded accent-blue-500" />
                <span className="text-xs text-slate-300">I confirm this information is true and accurate.</span>
              </label>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1 border-slate-600">Cancel</Button>
                <Button onClick={handleAddChild} disabled={!addChildAttested || addChildLoading} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {addChildLoading ? 'Adding...' : 'Add Child (+$10/mo)'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Child Detail Modal */}
      <ChildDetailModal
        isOpen={showChildDetail}
        onClose={() => setShowChildDetail(false)}
        child={selectedChild}
        getAuthHeader={getAuthHeader}
      />
    </AppLayout>
  );
}
